import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// HELPERS
// ============================================================

const normalizePanIds = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
};

// ============================================================
// FETCH ACCOUNT DASHBOARD
// ============================================================

export const fetchAccountDashboardThunk = createAsyncThunk(
  "accountDashboard/fetch",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const panIds = normalizePanIds(filters.pan_ids);

      const params = {
        // TenantSwitcher can select one or many Panchayaths.
        // Send them as CSV: 29 OR 11,27,29
        pan_ids: panIds.join(","),
        category: filters.category || "",
        from_date: filters.from_date || "",
        to_date: filters.to_date || "",
      };

      const res = await api.get("/api/v1/account-dashboard", {
        params,
      });

      if (res.data?.success === false) {
        return rejectWithValue(
          res.data?.message || "Failed to load Account Dashboard"
        );
      }

      return res.data?.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load Account Dashboard"
      );
    }
  }
);

// ============================================================
// SLICE
// ============================================================

const initialCards = {
  total_panchayaths: 0,
  active_panchayaths: 0,
  total_bills: 0,
  total_bill_amount: 0,
  total_invoice_amount: 0,
  total_received: 0,
  total_outstanding: 0,
  pending_bills: 0,
  invoiced_bills: 0,
  partially_paid_bills: 0,
  cleared_bills: 0,
};

const accountDashboardSlice = createSlice({
  name: "accountDashboard",

  initialState: {
    cards: initialCards,
    category_summary: [],
    panchayath_outstanding: [],
    recent_receipts: [],
    monthly_chart: [],
    loading: false,
    error: null,
  },

  reducers: {
    clearAccountDashboardError: (state) => {
      state.error = null;
    },

    clearAccountDashboard: (state) => {
      state.cards = { ...initialCards };
      state.category_summary = [];
      state.panchayath_outstanding = [];
      state.recent_receipts = [];
      state.monthly_chart = [];
      state.loading = false;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountDashboardThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchAccountDashboardThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.cards = action.payload?.cards || { ...initialCards };
        state.category_summary = action.payload?.category_summary || [];
        state.panchayath_outstanding =
          action.payload?.panchayath_outstanding || [];
        state.recent_receipts = action.payload?.recent_receipts || [];
        state.monthly_chart = action.payload?.monthly_chart || [];
      })

      .addCase(fetchAccountDashboardThunk.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to load Account Dashboard";
      });
  },
});

export const {
  clearAccountDashboardError,
  clearAccountDashboard,
} = accountDashboardSlice.actions;

export default accountDashboardSlice.reducer;
